// A helper that builds a comprehensive printable HTML for Emergency Visit
// Usage: const html = buildEmergencyPrintHtml({ visit, charges, fee, t, mediaUrl })

import { getHospitalName } from "../../../utils/hospitalInfo";

export function buildEmergencyPrintHtml({
  visit,
  charges,
  fee = 0,
  t,
  mediaUrl = "",
  attendingDoctor = null,
  dispositionDoctor = null,
}) {
  const fmt = (n) => Number(n || 0).toLocaleString();
  const hospitalName = getHospitalName(t);

  const triageText =
    {
      1: t("emergency.triage.red"),
      2: t("emergency.triage.yellow"),
      3: t("emergency.triage.green"),
    }[visit?.triage_level] || "-";

  const dispositionText =
    {
      1: t("emergency.disposition.discharge"),
      2: t("emergency.disposition.admit_ipd"),
      3: t("emergency.disposition.admit_icu"),
      4: t("emergency.disposition.referral"),
      5: t("emergency.disposition.death"),
    }[visit?.disposition] || "-";

  const triageClass =
    visit?.triage_level === 1
      ? "red"
      : visit?.triage_level === 2
      ? "yellow"
      : visit?.triage_level === 3
      ? "green"
      : "";

  const vitals = [
    visit?.bp
      ? `<div class="vital-item"><span class="vital-label">BP:</span> <span class="vital-value">${visit.bp}</span></div>`
      : "",
    visit?.hr
      ? `<div class="vital-item"><span class="vital-label">HR:</span> <span class="vital-value">${visit.hr}</span></div>`
      : "",
    visit?.rr
      ? `<div class="vital-item"><span class="vital-label">RR:</span> <span class="vital-value">${visit.rr}</span></div>`
      : "",
    visit?.temp_c
      ? `<div class="vital-item"><span class="vital-label">${t(
          "emergency.labels.temp_c"
        )}:</span> <span class="vital-value">${visit.temp_c}</span></div>`
      : "",
    visit?.spo2
      ? `<div class="vital-item"><span class="vital-label">${t(
          "emergency.labels.spo2"
        )}:</span> <span class="vital-value">${visit.spo2}</span></div>`
      : "",
  ]
    .filter(Boolean)
    .join("");

  const patientName = visit?.patient || visit?.walkin_name || "-";
  const patientAge = visit?.walkin_age ? `${visit.walkin_age}` : "";
  const patientPhone = visit?.walkin_phone || "";

  const totalCharges = (charges || []).reduce(
    (s, c) => s + Number(c.total_price || 0),
    0
  );
  const grandTotal = Number(fee || 0) + totalCharges;

  const rows = (charges || [])
    .map(
      (c, idx) =>
        `<tr>
          <td class="text-center">${idx + 1}</td>
          <td>${c.item_name || ""}</td>
          <td class="text-center">${c.quantity ?? ""}</td>
          <td class="text-right">${fmt(c.unit_price)}</td>
          <td class="text-right font-semibold">${fmt(c.total_price)}</td>
        </tr>`
    )
    .join("");

  const logoUrl = "/daudzai-logo.jpeg";

  const admitDetails =
    visit?.disposition === 2 || visit?.disposition === 3
      ? `<div class="info-grid">
         <div class="info-item"><span class="info-label">${t(
           "emergency.labels.destination_ward"
         )}:</span> <span class="info-value">${
          visit?.admit_destination || "-"
        }</span></div>
         <div class="info-item"><span class="info-label">${t(
           "emergency.labels.bed_no"
         )}:</span> <span class="info-value">${
          visit?.admit_bed_no || "-"
        }</span></div>
       </div>`
      : "";

  const refDetails =
    visit?.disposition === 4
      ? `<div class="info-grid">
         <div class="info-item"><span class="info-label">${t(
           "emergency.labels.referral_facility"
         )}:</span> <span class="info-value">${
          visit?.referral_facility || "-"
        }</span></div>
         <div class="info-item"><span class="info-label">${t(
           "emergency.labels.facility_contact"
         )}:</span> <span class="info-value">${
          visit?.referral_contact || "-"
        }</span></div>
         <div class="info-item"><span class="info-label">${t(
           "emergency.labels.receiving_doctor"
         )}:</span> <span class="info-value">${
          visit?.referral_doctor || "-"
        }</span></div>
       </div>`
      : "";

  const deathDetails =
    visit?.disposition === 5
      ? `<div class="info-grid">
         <div class="info-item"><span class="info-label">${t(
           "emergency.labels.time_of_death"
         )}:</span> <span class="info-value">${
          visit?.death_time || "-"
        }</span></div>
         <div class="info-item"><span class="info-label">${t(
           "emergency.labels.cause_of_death"
         )}:</span> <span class="info-value">${
          visit?.death_cause || "-"
        }</span></div>
       </div>`
      : "";

  return `
  <!DOCTYPE html>
  <html lang="en">
    <head>
      <meta charset="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <title>${hospitalName} - Emergency Visit #${visit?.id}</title>
      <style>
        @page { 
          size: A4; 
          margin: 10mm; 
        }
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body { 
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          color: #1e293b;
          line-height: 1.4;
          background: #ffffff;
          font-size: 11px;
        }
        
        /* Hospital Header - Made more compact */
        .hospital-header {
          background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
          color: white;
          padding: 12px 16px;
          border-radius: 8px 8px 0 0;
          margin-bottom: 0;
          box-shadow: 0 2px 4px rgba(30, 64, 175, 0.15);
        }
        
        .hospital-brand {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 8px;
        }
        
        /* Reduced logo size */
        .hospital-logo {
          width: 48px;
          height: 48px;
          background: white;
          border-radius: 8px;
          padding: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
        }
        
        .hospital-logo img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
        
        .hospital-logo-placeholder {
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          font-weight: 700;
          color: white;
        }
        
        .hospital-info {
          flex: 1;
        }
        
        /* Reduced font sizes */
        .hospital-name {
          font-size: 20px;
          font-weight: 700;
          letter-spacing: -0.3px;
          margin-bottom: 2px;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        }
        
        .hospital-tagline {
          font-size: 11px;
          opacity: 0.95;
          font-weight: 500;
        }
        
        .document-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 8px;
          border-top: 1px solid rgba(255, 255, 255, 0.3);
        }
        
        .document-title {
          font-size: 13px;
          font-weight: 600;
        }
        
        .document-meta {
          font-size: 10px;
          opacity: 0.9;
        }
        
        /* Content Container - Reduced padding */
        .content-wrapper {
          border: 2px solid #e2e8f0;
          border-top: none;
          border-radius: 0 0 8px 8px;
          padding: 14px;
          background: #ffffff;
        }
        
        /* Section Styling - Reduced margins */
        .section {
          margin-bottom: 12px;
        }
        
        .section:last-child {
          margin-bottom: 0;
        }
        
        .section-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
          padding-bottom: 6px;
          border-bottom: 2px solid #3b82f6;
        }
        
        /* Smaller section icons */
        .section-icon {
          width: 20px;
          height: 20px;
          background: linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%);
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 700;
          font-size: 11px;
        }
        
        .section-title {
          font-size: 12px;
          font-weight: 700;
          color: #1e40af;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }
        
        /* Info Grid - Reduced padding and gaps */
        .info-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 8px 16px;
          background: #f8fafc;
          padding: 10px;
          border-radius: 6px;
          border: 1px solid #e2e8f0;
        }
        
        .info-item {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        
        .info-label {
          display: block;
          font-size: 9px;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          margin-bottom: 2px;
        }
        
        .info-value {
          display: block;
          font-size: 11px;
          font-weight: 600;
          color: #1e293b;
        }
        
        /* Triage Badge - Made smaller */
        .triage-badge {
          display: inline-flex;
          align-items: center;
          padding: 3px 10px;
          border-radius: 12px;
          font-weight: 700;
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        }
        
        .triage-badge.red {
          background: #dc2626;
          color: white;
        }
        
        .triage-badge.yellow {
          background: #fbbf24;
          color: #78350f;
        }
        
        .triage-badge.green {
          background: #16a34a;
          color: white;
        }
        
        /* Vitals Section - More compact layout */
        .vitals-container {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 6px;
          margin-top: 8px;
        }
        
        .vital-item {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          padding: 6px;
          text-align: center;
        }
        
        .vital-label {
          display: block;
          font-size: 9px;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          margin-bottom: 2px;
        }
        
        .vital-value {
          display: block;
          font-size: 12px;
          font-weight: 700;
          color: #1e40af;
        }
        
        /* Clinical Notes - Reduced padding */
        .clinical-note {
          background: #eff6ff;
          border-left: 3px solid #3b82f6;
          padding: 8px 10px;
          border-radius: 4px;
          margin-top: 8px;
        }
        
        .clinical-note-label {
          font-size: 9px;
          font-weight: 700;
          color: #1e40af;
          text-transform: uppercase;
          margin-bottom: 4px;
          display: block;
        }
        
        .clinical-note-text {
          font-size: 10px;
          color: #1e293b;
          line-height: 1.4;
        }
        
        /* Table Styling - More compact table */
        .charges-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          margin-top: 8px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          overflow: hidden;
          font-size: 10px;
        }
        
        .charges-table thead {
          background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
          color: white;
        }
        
        .charges-table th {
          padding: 8px 10px;
          font-size: 9px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          text-align: left;
        }
        
        .charges-table td {
          padding: 6px 10px;
          font-size: 10px;
          border-bottom: 1px solid #e2e8f0;
        }
        
        .charges-table tbody tr:last-child td {
          border-bottom: none;
        }
        
        .charges-table tbody tr:hover {
          background: #f8fafc;
        }
        
        .text-center {
          text-align: center;
        }
        
        .text-right {
          text-align: right;
        }
        
        .font-semibold {
          font-weight: 600;
        }
        
        /* Totals Section - Reduced spacing */
        .totals-container {
          margin-top: 10px;
          display: flex;
          justify-content: flex-end;
        }
        
        .totals-box {
          background: #f8fafc;
          border: 2px solid #e2e8f0;
          border-radius: 6px;
          padding: 10px 14px;
          min-width: 280px;
        }
        
        .total-row {
          display: flex;
          justify-content: space-between;
          padding: 4px 0;
          font-size: 11px;
        }
        
        .total-row.subtotal {
          color: #64748b;
        }
        
        .total-row.grand-total {
          border-top: 2px solid #3b82f6;
          margin-top: 6px;
          padding-top: 8px;
          font-size: 14px;
          font-weight: 700;
          color: #1e40af;
        }
        
        /* Footer Signatures - Much more compact */
        .signatures {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
          margin-top: 20px;
          padding-top: 12px;
          border-top: 2px dashed #cbd5e1;
        }
        
        .signature-box {
          text-align: center;
        }
        
        .signature-line {
          border-top: 2px solid #1e40af;
          margin-bottom: 6px;
          padding-top: 24px;
        }
        
        .signature-label {
          font-size: 9px;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
        }
        
        /* Print Optimizations */
        @media print {
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          .hospital-header {
            break-inside: avoid;
          }
          
          .section {
            break-inside: avoid;
          }
          
          .charges-table {
            break-inside: auto;
          }
          
          .charges-table tr {
            break-inside: avoid;
          }
        }
      </style>
    </head>
    <body>
      <!-- Hospital Header -->
      <div class="hospital-header">
        <div class="hospital-brand">
          <div class="hospital-logo">
            ${
              logoUrl
                ? `<img src="${logoUrl}" alt="Mustashfa Omar Academic Hospital Logo" />`
                : `<div class="hospital-logo-placeholder">H</div>`
            }
          </div>
          <div class="hospital-info">
            <div class="hospital-name">${hospitalName}</div>
            <div class="hospital-tagline">Excellence in Emergency Care</div>
          </div>
        </div>
        <div class="document-info">
          <div class="document-title">Emergency Visit Report #${visit?.id}</div>
          <div class="document-meta">
            ${
              visit?.arrival_time || "-"
            } • Printed: ${new Date().toLocaleString()}
          </div>
        </div>
      </div>

      <!-- Content Wrapper -->
      <div class="content-wrapper">
        
        <!-- Patient & Triage Section -->
        <div class="section">
          <div class="section-header">
            <div class="section-icon">👤</div>
            <div class="section-title">Patient Information & Triage</div>
          </div>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">Patient Name</span>
              <span class="info-value">${patientName}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Triage Level</span>
              <span class="triage-badge ${triageClass}">${triageText}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Age</span>
              <span class="info-value">${patientAge || "-"}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Phone</span>
              <span class="info-value">${patientPhone || "-"}</span>
            </div>
            ${
              visit?.arrival_mode
                ? `
            <div class="info-item">
              <span class="info-label">Arrival Mode</span>
              <span class="info-value">${visit.arrival_mode}</span>
            </div>
            `
                : ""
            }
            <div class="info-item">
              <span class="info-label">Disposition</span>
              <span class="info-value">${dispositionText}</span>
            </div>
            ${
              attendingDoctor
                ? `
            <div class="info-item">
              <span class="info-label">Attending Doctor</span>
              <span class="info-value">${attendingDoctor.first_name} ${attendingDoctor.last_name}</span>
            </div>
            `
                : ""
            }
            ${
              dispositionDoctor
                ? `
            <div class="info-item">
              <span class="info-label">Diagnosing Doctor</span>
              <span class="info-value">${dispositionDoctor.first_name} ${dispositionDoctor.last_name}</span>
            </div>
            `
                : ""
            }
          </div>
          
          ${
            vitals
              ? `
          <div class="vitals-container">
            ${vitals}
          </div>
          `
              : ""
          }
          
          ${
            visit?.chief_complaint
              ? `
          <div class="clinical-note">
            <span class="clinical-note-label">${t(
              "emergency.labels.chief_complaint"
            )}</span>
            <div class="clinical-note-text">${visit.chief_complaint}</div>
          </div>
          `
              : ""
          }
          
          ${
            visit?.diagnosis
              ? `
          <div class="clinical-note">
            <span class="clinical-note-label">${t(
              "emergency.labels.diagnosis"
            )}</span>
            <div class="clinical-note-text">${visit.diagnosis}</div>
          </div>
          `
              : ""
          }
          
          ${
            visit?.disposition_notes
              ? `
          <div class="clinical-note">
            <span class="clinical-note-label">${
              t("emergency.labels.notes") || "Notes"
            }</span>
            <div class="clinical-note-text">${visit.disposition_notes}</div>
          </div>
          `
              : ""
          }
        </div>

        <!-- Disposition Details Section -->
        ${
          admitDetails || refDetails || deathDetails
            ? `
        <div class="section">
          <div class="section-header">
            <div class="section-icon">📋</div>
            <div class="section-title">Disposition Details</div>
          </div>
          ${admitDetails}${refDetails}${deathDetails}
        </div>
        `
            : ""
        }

        <!-- Charges Section -->
        <div class="section">
          <div class="section-header">
            <div class="section-icon">💰</div>
            <div class="section-title">Charges & Billing</div>
          </div>
          <table class="charges-table">
            <thead>
              <tr>
                <th style="width: 40px;">#</th>
                <th>Item Description</th>
                <th style="width: 60px;" class="text-center">Qty</th>
                <th style="width: 100px;" class="text-right">Unit Price</th>
                <th style="width: 100px;" class="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              ${
                rows ||
                '<tr><td colspan="5" class="text-center" style="color: #94a3b8;">No charges recorded</td></tr>'
              }
            </tbody>
          </table>
          
          <div class="totals-container">
            <div class="totals-box">
              <div class="total-row subtotal">
                <span>${t("emergency.labels.er_visit_fee")}:</span>
                <span>${fmt(fee)}</span>
              </div>
              <div class="total-row subtotal">
                <span>${t("emergency.labels.charges_total")}:</span>
                <span>${fmt(totalCharges)}</span>
              </div>
              <div class="total-row grand-total">
                <span>${t("emergency.labels.grand_total")}:</span>
                <span>${fmt(grandTotal)}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Signatures -->
        <div class="signatures">
          <div class="signature-box">
            <div class="signature-line"></div>
            <div class="signature-label">${t("emergency.print.prepared_by", {
              defaultValue: "Prepared By / Signature",
            })}</div>
          </div>
          <div class="signature-box">
            <div class="signature-line"></div>
            <div class="signature-label">${t("emergency.print.reviewed_by", {
              defaultValue: "Reviewed By / Signature",
            })}</div>
          </div>
        </div>
        
      </div>

      <script>window.print();</script>
    </body>
  </html>
  `;
}

